import React from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import get from 'lodash/get';

import { Button, Link } from '@nerdwallet/currency';
import AuthFlowButton from '@nerdwallet/react-auth-flow-button';
import AppButtons from '../../app-buttons/app-buttons';

import {
  repeatable,
  stringInputSpec,
  stringTextAreaSpec,
  stringSelectSpec,
  boolCheckboxSpec,
  group,
} from '../../../lib/editing-configs';
import { getButtonStyle } from '../../../lib/button-style-helper';
import UniversalText from '../../universal-text/universal-text';

export const NULL_BUTTON_TYPE = 'none';
export const CUSTOM_BUTTON_TYPE = 'custom';
const APP_STORE_BUTTON_TYPE = 'textMeApp';

function buttonTypeInputConfig(fieldName, defaultItemValue = '') {
  const items = [
    {
      label: 'None',
      value: NULL_BUTTON_TYPE,
    },
    {
      label: 'Custom URL',
      value: CUSTOM_BUTTON_TYPE,
    },
    {
      label: 'Dialog: Generic Onboarding',
      value: 'onboardingGeneric',
    },
    {
      label: 'Dialog: Credit Score',
      value: 'creditScore',
    },
    {
      label: 'Dialog: BillShark',
      value: 'bills',
    },
    {
      label: 'Dialog: Emergency Fund Goal',
      value: 'emergencyFundGoal',
    },
    {
      label: 'Dialog: Savings Goal',
      value: 'savingsGoal',
    },
    {
      label: 'Dialog: Retirement Goal',
      value: 'retirementGoal',
    },
    {
      label: 'Dialog: Debt Goal',
      value: 'debtGoal',
    },
    {
      label: 'Dialog: Credit Score Goal',
      value: 'creditGoal',
    },
    {
      label: 'Dialog: Cash Back',
      value: 'cashBack',
    },
    {
      label: 'App Download Buttons',
      value: APP_STORE_BUTTON_TYPE,
    },
  ];
  let sortedItems = [];
  if (defaultItemValue !== '') {
    sortedItems = items.sort((item) => (item.value === 'custom' ? -1 : 1));
  } else {
    sortedItems = items;
  }

  return stringSelectSpec(`${fieldName}.buttonType`, 'CTA', sortedItems);
}

const CtaButton = ({
  buttonClass,
  buttonType,
  buttonText,
  buttonURL,
  successURL,
  authDialogCopyOverrides,
  authDialogImageOverrides,
  appStoreURL,
  googlePlayURL,
  buttonParams,
  displayStyle,
  link,
  primary,
  disabled,
  loading,
  loadingMessage,
}) => {
  if (buttonType === NULL_BUTTON_TYPE) {
    return null;
  }

  const buttonStyle = getButtonStyle(primary);
  const displayBlock = displayStyle === 'block';

  const params = buttonParams.reduce(
    (paramDict, { queryKey, queryValue }) => ({
      ...paramDict,
      [queryKey]: queryValue,
    }),
    {}
  );

  if (buttonURL) {
    // Has user already included a query in their buttonURL?
    const [url, existingQuery = ''] = buttonURL.split('?');
    const newQuery =
      buttonParams.length > 0
        ? `?${existingQuery}&${queryString.stringify(params)}`
        : existingQuery && `?${existingQuery}`;
    const href = `${url}${newQuery}`;

    return link ? (
      <Link href={href} className={buttonClass}>
        {buttonText}
      </Link>
    ) : (
      <Button
        className={buttonClass}
        href={href}
        block={displayBlock}
        disabled={disabled}
        loading={loading}
        loadingMessage={loadingMessage}
        {...buttonStyle}
      >
        {buttonText}
      </Button>
    );
  }

  if (buttonType === APP_STORE_BUTTON_TYPE) {
    return (
      <AppButtons appStoreURL={appStoreURL} googlePlayURL={googlePlayURL} />
    );
  }

  const flowProps = {
    trk: params,
    flowConfig: {},
  };

  if (successURL) {
    flowProps.onSuccess = successURL;
  }

  if (authDialogCopyOverrides) {
    flowProps.flowConfig.copyOverrides = {};
    authDialogCopyOverrides.forEach((copyOverride) => {
      flowProps.flowConfig.copyOverrides[copyOverride.copyKey] =
        copyOverride.copyValue;
    });
  }

  if (authDialogImageOverrides) {
    flowProps.flowConfig.imageOverrides = {};
    authDialogImageOverrides.forEach((imageOverride) => {
      flowProps.flowConfig.imageOverrides[imageOverride.imageKey] =
        imageOverride.imageValue;
    });
  }

  return (
    <AuthFlowButton
      flowPreset={buttonType}
      flowProps={flowProps}
      className={buttonClass}
      block={displayBlock}
      {...buttonStyle}
    >
      {buttonText}
    </AuthFlowButton>
  );
};

CtaButton.displayName = 'CTAButton';

CtaButton.propTypes = {
  buttonClass: PropTypes.string,
  buttonType: PropTypes.string,
  buttonText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  buttonURL: PropTypes.string,
  successURL: PropTypes.string,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  loadingMessage: PropTypes.string,
  authDialogCopyOverrides: PropTypes.arrayOf(
    PropTypes.shape({
      copyKey: PropTypes.string,
      copyValue: PropTypes.string,
    })
  ),
  authDialogImageOverrides: PropTypes.arrayOf(
    PropTypes.shape({
      imageKey: PropTypes.string,
      imageValue: PropTypes.string,
    })
  ),
  buttonParams: PropTypes.array,
  displayStyle: PropTypes.oneOf(['inline-block', 'block']),
  link: PropTypes.bool,
  primary: PropTypes.string,
  appStoreURL: PropTypes.string,
  googlePlayURL: PropTypes.string,
};

CtaButton.defaultProps = {
  buttonType: NULL_BUTTON_TYPE,
  buttonParams: [],
  displayStyle: 'inline-block',
  link: false,
  primary: 'primary',
  loading: false,
  disabled: false,
  loadingMessage: '',
};

CtaButton.getEditingConfig = (
  componentConfig,
  fieldName,
  allowedControlsList = []
) => {
  const buttonType = get(componentConfig, `props.${fieldName}.buttonType`, '');
  const isAuthDialog =
    buttonType &&
    buttonType !== NULL_BUTTON_TYPE &&
    buttonType !== CUSTOM_BUTTON_TYPE &&
    buttonType !== APP_STORE_BUTTON_TYPE;

  const successURLSpec = isAuthDialog
    ? [
        stringInputSpec(`${fieldName}.successURL`, 'CTA on success url', {
          help: 'For Dialog CTAs, the URL to take the user after they have successfully registered',
        }),
      ]
    : [];

  const appStoreUrlOverridesSpec =
    buttonType === APP_STORE_BUTTON_TYPE ? AppButtons.getEditingConfig() : [];

  const displayStyleEditorControls = allowedControlsList.includes(
    'displayStyle'
  )
    ? [
        stringSelectSpec(
          `${fieldName}.displayStyle`,
          'Display',
          [
            { label: 'Inline', value: 'inline-block' },
            { label: 'Block', value: 'block' },
          ],
          {
            help: 'Defaults to "Inline"',
          }
        ),
      ]
    : [];

  const loadingEditorControls = allowedControlsList.includes('loading')
    ? [boolCheckboxSpec(`${fieldName}.loading`, 'Loading?')]
    : [];

  const disabledEditorControls = allowedControlsList.includes('disabled')
    ? [boolCheckboxSpec(`${fieldName}.disabled`, 'Disabled?')]
    : [];

  const loadingMessageEditorControls = allowedControlsList.includes(
    'loadingMessage'
  )
    ? [stringInputSpec(`${fieldName}.loadingMessage`, 'Loading Message?')]
    : [];

  const alignmentEditorControls = allowedControlsList.includes('alignment')
    ? [
        stringSelectSpec(
          `${fieldName}.alignment`,
          'Alignment',
          [
            { label: 'Left', value: 'left' },
            { label: 'Center', value: 'center' },
            { label: 'Right', value: 'right' },
          ],
          {
            help: 'Defaults to "Left"',
          }
        ),
      ]
    : [];

  return [
    buttonTypeInputConfig(fieldName, buttonType ? '' : CUSTOM_BUTTON_TYPE),
    stringTextAreaSpec(`${fieldName}.buttonText`, 'CTA Text'),
    ...successURLSpec,
    ...appStoreUrlOverridesSpec,
    stringInputSpec(`${fieldName}.buttonURL`, 'Custom button target url', {
      help: 'URL for the user to be redirected to on click. Be sure to add tracking params (e.g. www.a.com?trk_app=landing-page-client)',
    }),

    group('Properties', [
      stringSelectSpec(
        `${fieldName}.primary`,
        'Button Style',
        [
          { label: 'Default', value: '' },
          { label: 'Primary', value: 'primary' },
          { label: 'Secondary', value: 'secondary' },
          { label: 'Tertiary', value: 'tertiary' },
        ],
        {
          help: 'Primary output a GREEN button with WHITE text; Secondary output a WHITE button with GREEN text, Tertiary output a WHITE button without border',
        }
      ),
      ...disabledEditorControls,
      ...loadingEditorControls,
      ...loadingMessageEditorControls,
      ...displayStyleEditorControls,
      ...alignmentEditorControls,
    ]),
    ...UniversalText.getEditingConfig(
      componentConfig,
      'Extra Text',
      [
        'textSize',
        'textType',
        'fontWeight',
        'padding',
        'margin',
        'groupButton',
        'textChronicle',
        'textColor',
      ],
      `${fieldName}.ctaExtraText`
    ),

    group('Tracking', [
      stringInputSpec(`${fieldName}.componentName`, 'Component Name', {
        help: 'For Tracking Purposes',
      }),
      repeatable(
        `${fieldName}.buttonParams`,
        'Tracking Params',
        'Param',
        () => [
          stringInputSpec(`queryKey`, 'Key'),
          stringTextAreaSpec(`queryValue`, 'Value'),
        ]
      ),
    ]),
  ];
};

export default CtaButton;
